import { useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import type { ModalProps, ModalSize } from './types'
import { cn } from '@/shared/cn'
import { Button } from '@/components/ui/Button'

const OVERLAY =
  'fixed inset-0 z-[9998] flex items-center justify-center transition-opacity duration-200'

const CARD =
  'relative z-[9999] flex w-full max-h-[90vh] flex-col shadow-xl transition-all duration-200'

const SIZE_MAX_WIDTH: Record<ModalSize, string> = {
  xxs: 'max-w-[320px]',
  xs: 'max-w-[400px]',
  sm: 'max-w-[500px]',
  md: 'max-w-[600px]',
  lg: 'max-w-[900px]',
}

export function Modal({
  open,
  onClose,
  title,
  children,
  actions,
  size = 'md',
  dismissible = true,
  zIndex,
}: ModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (dismissible && e.key === 'Escape') onClose()
    },
    [onClose, dismissible],
  )

  useEffect(() => {
    if (!open) return
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [open, handleKeyDown])

  if (!open) return null

  return createPortal(
    <div
      className={cn(OVERLAY, open ? 'opacity-100' : 'opacity-0')}
      style={{ backgroundColor: '#00000040', ...(zIndex != null && { zIndex }) }}
      onClick={dismissible ? onClose : undefined}
      data-test-id="modal-overlay"
    >
      <div
        className={cn(CARD, SIZE_MAX_WIDTH[size], open ? 'scale-100 opacity-100' : 'scale-95 opacity-0')}
        style={{ borderRadius: 16, background: '#fff', ...(zIndex != null && { zIndex: zIndex + 1 }) }}
        onClick={(e) => e.stopPropagation()}
        data-test-id="modal-card"
      >
        {/* ── Header ─────────────────────────────────── */}
        <div
          className="flex items-center justify-between"
          data-test-id="modal-header"
          style={{
            padding: '24px 22px',
            borderBottom: '1px solid #E0E0E0',
          }}
        >
          <h2
            className="font-bold"
            style={{ color: '#005A9E', fontSize: 24, lineHeight: 1.2, margin: 0 }}
          >
            {title}
          </h2>
          {dismissible && (
            <button
              type="button"
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-gray-100"
              onClick={onClose}
              aria-label="Fechar"
              data-test-id="modal-btn-close"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 1l12 12M1 13L13 1" />
              </svg>
            </button>
          )}
        </div>

        {/* ── Body ──────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto" style={{ padding: '24px 22px' }} data-test-id="modal-body">
          {children}
        </div>

        {/* ── Footer ─────────────────────────────────── */}
        {actions && actions.length > 0 && (
          <div
            className="flex items-center justify-end gap-3"
            data-test-id="modal-footer"
            style={{
              padding: '24px 22px',
              borderTop: '1px solid #E0E0E0',
            }}
          >
            {actions.map((act) => (
              <Button
                key={act.label}
                variant={act.variant ?? 'outline'}
                size="sm"
                onClick={act.action}
              >
                {act.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
